"use client";

import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { DateTime } from "luxon";
import {
  Edit,
  Trash2,
  Clock,
  MapPin,
  Video,
  User,
  Users,
  ExternalLink,
  Mail,
} from "lucide-react";
import { Badge } from "../badge.js";
import { Button } from "../button.js";
import { CalendarEvent } from "../../types/calendar.js";
import { EventDetailsTabs } from "./EventDetailsTabs.js";
import { EventEditDialog } from "./EventEditDialog.js";
import { ResponsiveModal } from "../responsive-modal.js";
import { useCalendarContext } from "../../lib/calendar-context.js";
import {
  EventFormGrid,
  EventFormPrimaryColumn,
  EventFormSidebar,
  EventFormSection,
} from "./calendarinvites/shared/EventFormShell.js";

interface EventDetailsModalProps {
  event: CalendarEvent | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent) => void;
  // CRM Integration props
  onLogActivity?: (activityData: any) => Promise<void>;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  // Custom CRM tab content
  customCRMTab?: React.ReactNode;
}

/**
 * Persistent left summary rail — the "key facts" at a glance, always visible
 * regardless of which tab is active. Pulls the When / Location / Online meeting /
 * Organizer / Attendees / Source facts OUT of the Details tab so they are not
 * duplicated. Read-only display only.
 */
function EventSummaryRail({ event }: { event: CalendarEvent }) {
  const formatTimeRange = (start: string, end: string): string => {
    const timeZone = event.timeZone || "America/Chicago";
    const startDateTime = DateTime.fromISO(start, { zone: timeZone });
    const endDateTime = DateTime.fromISO(end, { zone: timeZone });

    if (startDateTime.isValid && endDateTime.isValid) {
      const localStart = startDateTime.toLocal();
      const localEnd = endDateTime.toLocal();
      return `${localStart.toLocaleString(DateTime.DATETIME_FULL)} - ${localEnd.toLocaleString(
        DateTime.DATETIME_FULL,
      )}`;
    }

    // Fallback to date-fns if Luxon fails to parse
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${format(startDate, "PPP")} • ${format(startDate, "p")} - ${format(endDate, "p")}`;
  };

  const organizerName = event.organizerData?.name || event.organizerData?.email || event.organizer;
  const organizerEmail = event.organizerData?.email || event.organizerEmail || event.organizer;

  const attendeeCount =
    event.attendeeData?.length ?? event.attendees?.length ?? 0;

  const joinUrl = event.onlineMeetingUrl || event.webLink || event.htmlLink;

  return (
    <EventFormSidebar>
      {/* When */}
      <EventFormSection title="When" icon={Clock}>
        <p className="text-sm text-slate-700">{formatTimeRange(event.start, event.end)}</p>
        {event.allDay && (
          <Badge
            variant="secondary"
            className="mt-2 bg-slate-100 text-slate-800 border-gray-300"
          >
            All day event
          </Badge>
        )}
        {event.isCancelled && (
          <Badge variant="destructive" className="mt-2 text-xs">
            Event Cancelled
          </Badge>
        )}
      </EventFormSection>

      {/* Location */}
      {event.location && (
        <EventFormSection title="Location" icon={MapPin}>
          <p className="text-sm text-slate-700 break-words">{event.location}</p>
        </EventFormSection>
      )}

      {/* Online meeting / Join */}
      {joinUrl && (
        <EventFormSection title="Online meeting" icon={Video}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => window.open(joinUrl, "_blank")}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            <span>Join</span>
          </Button>
        </EventFormSection>
      )}

      {/* Organizer */}
      {(organizerName || organizerEmail) && (
        <EventFormSection title="Organizer" icon={User}>
          <div className="flex items-center gap-2">
            <div className="min-w-0">
              <p className="text-sm text-slate-700 truncate">{organizerName}</p>
              {organizerEmail && organizerEmail !== organizerName && (
                <p className="text-xs text-muted-foreground truncate">{organizerEmail}</p>
              )}
            </div>
            {organizerEmail && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => window.open(`mailto:${organizerEmail}`, "_blank")}
                className="h-6 px-2 text-xs shrink-0"
                aria-label="Email organizer"
              >
                <Mail className="h-3 w-3 mr-1" />
                Email
              </Button>
            )}
          </div>
        </EventFormSection>
      )}

      {/* Attendees + Source */}
      <EventFormSection title="At a glance" icon={Users}>
        <div className="space-y-2 text-sm text-slate-700">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Attendees</span>
            <span className="font-medium">{attendeeCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Source</span>
            <Badge className="bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 text-xs px-2 py-0.5 capitalize">
              {event.source}
            </Badge>
          </div>
        </div>
      </EventFormSection>
    </EventFormSidebar>
  );
}

export function EventDetailsModal({
  event,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onLogActivity,
  onComposeEmail,
  customCRMTab,
}: EventDetailsModalProps) {
  // Get contact search functionality from CalendarContext
  const { contacts, onSearchContacts, isLoadingContacts } = useCalendarContext();

  const [mounted, setMounted] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleEdit = (eventToEdit: CalendarEvent) => {
    setShowEditDialog(true);
    onEdit?.(eventToEdit);
  };

  const handleDelete = async (eventToDelete: CalendarEvent) => {
    setIsDeleting(true);
    try {
      await onDelete?.(eventToDelete);
      onClose();
    } catch (error) {
      console.error("Failed to delete event:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getSourceColor = (_source: string) => {
    // Simplified, neutral style for source badges
    return "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-100";
  };

  if (!event || !mounted) return null;

  // Custom header content for mobile drawer
  const customHeaderContent = (
    <div className="flex items-center justify-between gap-3 w-full">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <h3 className="text-lg font-semibold truncate">
          {event.title}
        </h3>
        <Badge className={`${getSourceColor(event.source)} text-xs px-2 py-1 shrink-0`}>
          {event.source}
        </Badge>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {onEdit && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(event)}
            className="h-8 w-8 p-0"
            aria-label="Edit event"
          >
            <Edit className="h-4 w-4" />
          </Button>
        )}
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(event)}
            disabled={isDeleting}
            className="h-8 w-8 p-0 event-delete-button"
            aria-label="Delete event"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ResponsiveModal
        open={isOpen}
        onOpenChange={onClose}
        maxWidth="90vw"
        side="bottom"
        showCloseButton={true}
        title={event.title}
        description={`${event.source} Event`}
      >
        {/* Desktop: Custom header with actions for better layout */}
        <div className="md:flex md:items-center md:justify-between md:gap-3 md:mb-4 hidden">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-semibold truncate">
              {event.title}
            </h2>
            <Badge className={`${getSourceColor(event.source)} text-xs px-2 py-1 shrink-0`}>
              {event.source}
            </Badge>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onEdit && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(event)}
                className="h-8 w-8 p-0"
                aria-label="Edit event"
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(event)}
                disabled={isDeleting}
                className="h-8 w-8 p-0 event-delete-button"
                aria-label="Delete event"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Mobile: Enhanced header integrated into drawer header */}
        <div className="md:hidden mb-4">
          {customHeaderContent}
        </div>

        {/* Two-pane master/detail: persistent summary rail + tabbed content.
            Stacks to a single column (rail on top, then tabs) on mobile. */}
        <EventFormGrid>
          <EventSummaryRail event={event} />
          <EventFormPrimaryColumn>
            <EventDetailsTabs
              event={event}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onClose={onClose}
              isPageView={false}
              onLogActivity={onLogActivity}
              onComposeEmail={onComposeEmail}
              customCRMTab={customCRMTab}
            />
          </EventFormPrimaryColumn>
        </EventFormGrid>
      </ResponsiveModal>

      <EventEditDialog
        event={event}
        isOpen={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        contacts={contacts}
        onSearchContacts={onSearchContacts}
        isLoadingContacts={isLoadingContacts}
      />
    </>
  );
}
